/**
 * OAuthCredentialsPage — admin-only page to store the platform Developer-App
 * credentials (Meta App ID + Secret, TikTok Client Key/Secret, LinkedIn Client ID/Secret)
 * directly in the encrypted DB, without touching the backend .env.
 *
 * Backed by:
 *   GET    /api/admin/oauth-credentials
 *   PUT    /api/admin/oauth-credentials/{platform}
 *   DELETE /api/admin/oauth-credentials/{platform}
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Trash2, Eye, EyeOff, Copy, Check, ShieldCheck, KeyRound, ExternalLink, FlaskConical, CheckCircle2, XCircle, ListOrdered, ChevronDown, ChevronUp } from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  meta: {
    label: "Meta (Instagram + Facebook)",
    color: "#1877F2",
    portal: "https://developers.facebook.com/apps",
    portalLabel: "developers.facebook.com",
    idLabel: "App ID",
    secretLabel: "App Secret",
    docNote: "Du brauchst eine Business-App mit den Use Cases 'Manage everything on your Page' + 'Instagram API with Instagram Login'.",
    steps: [
      { title: "App erstellen", text: "Auf developers.facebook.com/apps → 'App erstellen' → Use Case 'Sonstiges' → App-Typ 'Business' wählen. App-Name z. B. 'InvictaFlow'." },
      { title: "Produkte hinzufügen", text: "Im App-Dashboard das Produkt 'Facebook Login for Business' hinzufügen. Zusätzlich die Use Cases 'Manage everything on your Page' und 'Instagram API with Instagram Login' aktivieren." },
      { title: "Redirect URI eintragen", text: "Unter Facebook Login → Einstellungen → 'Valid OAuth Redirect URIs' die unten angezeigte Redirect URI einfügen (Copy-Button nutzen) und speichern." },
      { title: "App ID + Secret kopieren", text: "Unter Einstellungen → Allgemein findest du die App-ID und das App-Geheimcode (Secret). Beides oben eintragen → Speichern → 'Prüfen' klicken." },
      { title: "Entwicklungsmodus beachten", text: "Solange die App im Entwicklungsmodus ist, können sich nur App-Admins/Tester verbinden. Füge dich unter Rollen als Admin hinzu. Für Kunden-Accounts später den App-Review für pages_manage_posts & instagram_content_publish durchlaufen." },
      { title: "App auf LIVE? Dann Config-ID nötig!", text: "Sobald eine Business-App auf LIVE geschaltet ist, blockiert Facebook den klassischen Login mit Scopes („Sorry, this content isn't available right now“). Lösung: Im App-Dashboard → Facebook Login for Business → Configurations → '+ Create configuration' → Login-Variante 'User access token' wählen, alle benötigten Berechtigungen (pages_show_list, pages_read_engagement, pages_manage_posts, instagram_basic, instagram_content_publish, ads_management, ads_read, leads_retrieval) anhaken → speichern. Die angezeigte Configuration ID unten im Feld 'Login-Konfigurations-ID' eintragen → Speichern → erneut verbinden." },
    ],
  },
  tiktok: {
    label: "TikTok",
    color: "#000000",
    portal: "https://developers.tiktok.com/apps",
    portalLabel: "developers.tiktok.com",
    idLabel: "Client Key",
    secretLabel: "Client Secret",
    docNote: "Im TikTok-Dev-Portal: App anlegen + Produkte 'Login Kit' und 'Content Posting API' aktivieren.",
    steps: [
      { title: "App erstellen", text: "Auf developers.tiktok.com/apps → 'Create app'. Bei 'Ownership' wähle Individual (empfohlen — keine Firmen-Verifizierung nötig, reicht für Auto-Publishing völlig aus) → Confirm." },
      { title: "App-Details ausfüllen", text: "Trage die folgenden Werte ein (Achtung: Description max. 120 Zeichen!). Die Terms- und Privacy-Seiten sind bereits in InvictaFlow eingebaut und öffentlich erreichbar:", copyItems: [
        { label: "App-Name", value: "InvictaFlow" },
        { label: "Description (max. 120 Zeichen)", value: "Our internal marketing platform to plan, create and publish social media content for our group companies." },
        { label: "Kategorie", value: "Business" },
        { label: "Terms of Service URL", value: `${window.location.origin}/terms` },
        { label: "Privacy Policy URL", value: `${window.location.origin}/privacy` },
        { label: "Web/Desktop URL", value: `${window.location.origin}/` },
      ] },
      { title: "URL verifizieren (URL properties)", text: "TikTok verlangt eine Verifizierung der Domain. Oben rechts 'URL properties' → 'URL prefix' wählen → die Basis-URL dieser App eintragen. TikTok gibt dir eine Signature-Datei (tiktok….txt) — diese muss im Hauptverzeichnis der App liegen. Für die Preview-Umgebung ist sie bereits eingebaut; für eine neue Domain schickst du die Datei dem Agenten im Chat, er baut sie ein. Danach bei TikTok 'Verify' klicken." },
      { title: "Produkte hinzufügen", text: "Unter 'Products' → 'Add products' NUR diese drei: Login Kit (Konto verbinden), Content Posting API (posten — dort den Schalter 'Direct Post' EINSCHALTEN, sonst fehlt video.publish!) und Display API (Analytics: Views, Likes etc.). NICHT hinzufügen: Share Kit, Webhooks, Data Portability API — unnötige Produkte verzögern den Review." },
      { title: "Redirect URI eintragen", text: "Im Login Kit unter 'Redirect URI' (Tab 'Web') die unten angezeigte URI einfügen (Copy-Button nutzen) und 'Add a URI' klicken." },
      { title: "Scopes aktivieren", text: "Es müssen am Ende genau diese 5 Scopes aktiv sein: user.info.basic, video.upload, video.publish (kommt durch Direct Post), user.info.stats und video.list (für Analytics). user.info.profile wird NICHT benötigt." },
      { title: "Sandbox zum Testen (kein Video nötig)", text: "Oben vom Tab 'Production' auf 'Sandbox' wechseln → Sandbox erstellen → deinen eigenen TikTok-Account als 'Target User' hinzufügen. Wichtig: Die Sandbox hat ihre EIGENEN Client Key + Secret! Diese unten eintragen und die Verbindung testen. Posts erscheinen im Sandbox-Modus nur privat (SELF_ONLY) — perfekt zum Testen." },
      { title: "⚠️ Anderes Konto verbinden — Fehler 'non_sandbox_target'", text: "Erscheint NACH dem TikTok-Login die Meldung 'Es ist etwas schiefgelaufen / non_sandbox_target', ist das eingeloggte Konto nicht in der Sandbox freigegeben. Lösung: Im Dev-Portal → deine App → Sandbox → 'Target Users' → den TikTok-Benutzernamen des gewünschten Kontos hinzufügen (max. 10 Konten). Danach erneut verbinden. Erst nach bestandenem App-Review (Production) kann sich JEDES TikTok-Konto ohne diese Liste verbinden." },
      { title: "App-Review-Text einfügen", text: "Im Production-Formular unter 'App review' → 'Explain how each product and scope works' (max. 1000 Zeichen) diesen Text einfügen:", copyItems: [
        { label: "Review-Erklärung (EN)", value: "InvictaFlow is an internal marketing management platform used by our group companies (InvictaX Group, Switzerland) to plan, create and publish social media content to their own TikTok business accounts. Login Kit (user.info.basic): Our team members connect their company's own TikTok account via OAuth. We only read basic profile info to show which account is connected. Content Posting API (video.upload, video.publish): Authorized users create and schedule marketing videos inside InvictaFlow. At the scheduled time, the platform publishes the video directly to the connected TikTok account via Direct Post. Users review and approve every post before publishing. Display API (user.info.stats, video.list): We read follower counts and video statistics (views, likes, comments, shares) of the connected account's own videos to show performance analytics in our dashboard. The integration is web-only. Each TikTok account is connected and managed by its legitimate owner within our organization." },
      ] },
      { title: "Demo-Video + Review (nur für Production)", text: "Zum Speichern des Production-Formulars verlangt TikTok ein Demo-Video (mp4, Bildschirmaufnahme des kompletten Ablaufs: TikTok in InvictaFlow verbinden → Consent-Screen → Post erstellen → veröffentlichen → Post erscheint auf TikTok). Erst Sandbox-Test machen, dann Video aufnehmen, hochladen → Save → 'Submit for review'. Nach der Freigabe die Production-Credentials unten eintragen." },
    ],
  },
  pinterest: {
    label: "Pinterest",
    color: "#E60023",
    portal: "https://developers.pinterest.com/apps",
    portalLabel: "developers.pinterest.com",
    idLabel: "App ID",
    secretLabel: "App Secret",
    docNote: "Du brauchst ein Pinterest-BUSINESS-Konto (gratis umwandelbar). Im Dev-Portal eine App mit Trial-Access anlegen.",
    steps: [
      { title: "Business-Konto", text: "Pinterest-Konto in ein Business-Konto umwandeln (pinterest.com → Einstellungen → Kontoverwaltung → 'In Business-Konto umwandeln') und die E-Mail-Adresse verifizieren." },
      { title: "App erstellen", text: "Auf developers.pinterest.com → 'My apps' → 'Connect app' → Formular ausfüllen und Trial-Access beantragen (Freigabe meist innert 1-2 Tagen). Nach der Freigabe zeigt die App-Seite App ID + App Secret." },
      { title: "Redirect URI eintragen", text: "In der App-Konfiguration unter 'Redirect URIs' die unten angezeigte Redirect URI EXAKT eintragen (Copy-Button nutzen) und speichern." },
      { title: "Scopes", text: "Benötigte Scopes: user_accounts:read, boards:read, boards:write, pins:read, pins:write — diese werden beim Verbinden automatisch angefragt." },
      { title: "App ID + Secret eintragen", text: "App ID und App Secret oben eintragen → Speichern → 'Prüfen' klicken. Danach unter Social Media pro Firma 'Verbinden' klicken und das Board wählen, auf das gepostet werden soll." },
      { title: "Trial-Limits beachten", text: "Mit Trial-Access sind max. 300 Schreibzugriffe/Tag möglich und neu erstellte Pins sind teils nur für das eigene Konto sichtbar. Für den vollen Produktiv-Betrieb im Dev-Portal 'Standard access' beantragen (Pinterest verlangt dafür ein kurzes Demo-Video des OAuth-Flows)." },
    ],
  },
  linkedin: {
    label: "LinkedIn",
    color: "#0A66C2",
    portal: "https://www.linkedin.com/developers/apps",
    portalLabel: "linkedin.com/developers",
    idLabel: "Client ID",
    secretLabel: "Client Secret",
    docNote: "Im LinkedIn-Dev-Portal: App erstellen + Produkte 'Sign In with LinkedIn' und 'Share on LinkedIn' hinzufügen.",
    steps: [
      { title: "App erstellen", text: "Auf linkedin.com/developers/apps → 'Create app'. Du musst die App mit einer LinkedIn-Unternehmensseite verknüpfen (z. B. der Seite deiner Firma)." },
      { title: "Produkte hinzufügen", text: "Im Tab 'Products': 'Sign In with LinkedIn using OpenID Connect' und 'Share on LinkedIn' anfragen. Für Posts im Namen von Unternehmensseiten zusätzlich 'Community Management API'." },
      { title: "Redirect URL eintragen", text: "Im Tab 'Auth' unter 'Authorized redirect URLs' die unten angezeigte Redirect URI einfügen (Copy-Button nutzen)." },
      { title: "Client ID + Secret kopieren", text: "Ebenfalls im Tab 'Auth' findest du Client ID und Client Secret. Beides oben eintragen → Speichern → 'Prüfen' klicken." },
      { title: "Verifizierung", text: "LinkedIn verlangt ggf. eine Verifizierung der App durch einen Admin der verknüpften Unternehmensseite (Tab 'Settings' → 'Verify'). Erst danach werden die Produkte freigeschaltet." },
    ],
  },
};

export default function OAuthCredentialsPage() {
  const [creds, setCreds] = useState({});
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/oauth-credentials`, { withCredentials: true });
      setCreds(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  return (
    <div data-testid="oauth-credentials-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3">
          <KeyRound className="w-8 h-8 text-[#2563EB]" /> OAuth-App-Credentials
        </h1>
        <p className="text-[var(--studio-text-muted)] mt-1">
          Hinterlege die App-ID + Secret deiner Developer-Apps bei Meta / TikTok / LinkedIn — verschluesselt in der DB gespeichert, kein Redeploy noetig.
        </p>
      </div>

      <PageHelp title="Wozu brauche ich diese Seite?" storageKey="oauth-credentials" defaultOpen>
        <p>
          Damit deine Kunden ihre Instagram-/Facebook-/TikTok-/LinkedIn-Konten ueber die <a href="/social-media">Social-Media-Seite</a> verbinden koennen,
          musst du <b>einmalig pro Plattform eine Developer-App registrieren</b>. Du bekommst dort eine App-ID und ein Secret und traegst diese hier ein.
        </p>
        <h4>Reihenfolge pro Plattform</h4>
        <ol>
          <li>Im Developer-Portal der Plattform (z. B. <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com</a>) eine App anlegen.</li>
          <li>Die OAuth-Redirect-URI eintragen — Beispiel-URI siehst du unten pro Plattform unter „Redirect URI".</li>
          <li>App ID und App Secret kopieren und hier einfuegen → Speichern.</li>
          <li>Auf der <a href="/social-media">Social-Media-Seite</a> ist die Plattform jetzt freigeschaltet → Kunden koennen sich verbinden.</li>
        </ol>
        <p>
          ⚠ Diese Werte gelten <b>fuer alle Firmen</b> in deinem Studio — das ist deine Developer-Identitaet bei der Plattform, nicht die der Kunden.
          Kunden-Login passiert getrennt ueber den OAuth-Flow.
        </p>
      </PageHelp>

      <div className="space-y-4">
        {Object.entries(PLATFORM_META).map(([key, meta]) => (
          <PlatformCard
            key={key}
            platform={key}
            meta={meta}
            data={creds[key] || {}}
            onSaved={load}
            onCopy={copyToClipboard}
            copied={copied}
          />
        ))}
      </div>
    </div>
  );
}

function PlatformCard({ platform, meta, data, onSaved, onCopy, copied }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [scopes, setScopes] = useState("");
  const [configId, setConfigId] = useState("");

  const validate = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await axios.post(`${API}/admin/oauth-credentials/${platform}/validate`, {}, { withCredentials: true });
      setCheckResult(r.data);
    } catch (e) {
      setCheckResult({ valid: false, message: e?.response?.data?.detail || "Pruefung fehlgeschlagen" });
    }
    setChecking(false);
  };

  useEffect(() => {
    // When existing record present, prefill with sentinel so user can edit one of the fields
    if (data.has_client_id) setClientId("********");
    else setClientId("");
    if (data.has_client_secret) setClientSecret("********");
    else setClientSecret("");
    setScopes(data.scopes || "");
    setConfigId(data.config_id || "");
  }, [data.has_client_id, data.has_client_secret, data.scopes, data.config_id]);

  const save = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.info("Bitte beide Felder ausfuellen");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/oauth-credentials/${platform}`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          scopes: scopes.trim(),
          ...(platform === "meta" ? { config_id: configId.trim() } : {}),
        },
        { withCredentials: true }
      );
      toast.success(`${meta.label} gespeichert`);
      setShowSecret(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm(`${meta.label} wirklich entfernen? Verbindungen bestehender Konten bleiben aktiv, aber neue OAuth-Logins funktionieren nicht mehr.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/oauth-credentials/${platform}`, { withCredentials: true });
      toast.success(`${meta.label} entfernt`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Loeschen fehlgeschlagen");
    }
    setDeleting(false);
  };

  const status =
    data.source === "db"
      ? { cls: "bg-green-100 text-green-800", label: "Hinterlegt (DB)", testid: "status-active-db" }
      : data.source === "env"
      ? { cls: "bg-blue-100 text-blue-800", label: "Hinterlegt (ENV)", testid: "status-active-env" }
      : { cls: "bg-amber-100 text-amber-800", label: "Nicht konfiguriert", testid: "status-missing" };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid={`oauth-card-${platform}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
            {meta.label}
          </span>
          <Badge className={`${status.cls} text-[11px] font-mono`} data-testid={status.testid}>
            {data.source === "db" && <ShieldCheck className="w-3 h-3 mr-1 inline" />}
            {status.label}
            {data.source === "db" && data.client_id_preview && (
              <span className="ml-2 text-[10px] opacity-80">{data.client_id_preview}</span>
            )}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-[var(--studio-text-muted)]">{meta.docNote}</p>

        {meta.steps && (
          <div className="rounded-md border border-[var(--studio-border)]">
            <button
              type="button"
              onClick={() => setGuideOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-[#2563EB] hover:bg-[var(--studio-bg)] rounded-md"
              data-testid={`${platform}-setup-guide-toggle`}
            >
              <span className="flex items-center gap-1.5">
                <ListOrdered className="w-3.5 h-3.5" />
                Schritt-für-Schritt-Anleitung: {meta.label.split(" ")[0]}-App einrichten
              </span>
              {guideOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {guideOpen && (
              <ol className="px-3 pb-3 pt-1 space-y-2.5" data-testid={`${platform}-setup-guide`}>
                {meta.steps.map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-xs">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-[10px] font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{step.title}</div>
                      <div className="text-[var(--studio-text-muted)] leading-relaxed">{step.text}</div>
                      {step.copyItems && (
                        <div className="mt-2 space-y-1.5">
                          {step.copyItems.map((item) => (
                            <div key={item.label} className="flex items-center gap-2 rounded bg-[var(--studio-bg)] px-2 py-1.5">
                              <div className="min-w-0 flex-1">
                                <div className="text-[9px] uppercase font-semibold text-[var(--studio-text-muted)]">{item.label}</div>
                                <div className="font-mono text-[11px] break-all">{item.value}</div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 w-6 p-0 shrink-0"
                                onClick={() => onCopy(item.value, `${platform}-${item.label}`)}
                                data-testid={`copy-${platform}-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              >
                                {copied === `${platform}-${item.label}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">{meta.idLabel}</label>
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={meta.idLabel}
              className="mt-1 font-mono text-xs"
              data-testid={`${platform}-client-id`}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">{meta.secretLabel}</label>
            <div className="relative mt-1">
              <Input
                type={showSecret ? "text" : "password"}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={meta.secretLabel}
                className="font-mono text-xs pr-9"
                data-testid={`${platform}-client-secret`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--studio-text-muted)] hover:text-black"
                data-testid={`${platform}-toggle-secret`}
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded bg-[var(--studio-bg)] p-2 flex items-center justify-between gap-2 text-xs">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase font-semibold text-[var(--studio-text-muted)] mb-0.5">
              OAuth Redirect URI (im Developer-Portal eintragen)
            </div>
            <div className="font-mono break-all">{data.redirect_uri}</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] shrink-0"
            onClick={() => onCopy(data.redirect_uri, `${platform}-uri`)}
            data-testid={`copy-redirect-${platform}`}
          >
            {copied === `${platform}-uri` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-[var(--studio-text-muted)]">
              Angefragte Scopes (müssen zur App im Developer-Portal passen)
              {data.has_scopes_override && (
                <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]" data-testid={`${platform}-scopes-override-badge`}>angepasst</Badge>
              )}
            </label>
            {scopes !== data.default_scopes && (
              <button
                type="button"
                onClick={() => setScopes(data.default_scopes || "")}
                className="text-[10px] text-[#2563EB] hover:underline"
                data-testid={`${platform}-scopes-reset`}
              >
                Standard wiederherstellen
              </button>
            )}
          </div>
          <Input
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
            className="mt-1 font-mono text-xs"
            data-testid={`${platform}-scopes-input`}
          />
          <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
            Fehlermeldung „scope" beim Verbinden? Dann hier nur die Scopes eintragen, die in der App (bzw. Sandbox) tatsächlich aktiviert sind — speichern und erneut verbinden.
          </p>
        </div>

        {platform === "meta" && (
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">
              Login-Konfigurations-ID (Facebook Login for Business)
              {!!(data.config_id || "").trim() && (
                <Badge className="ml-2 bg-green-100 text-green-800 text-[10px]" data-testid="meta-config-id-badge">aktiv</Badge>
              )}
            </label>
            <Input
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder="z. B. 123456789012345"
              className="mt-1 font-mono text-xs"
              data-testid="meta-config-id-input"
            />
            <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
              Pflicht, wenn deine Business-App auf LIVE steht (Fehler „Sorry, this content isn't available right now"). Zu finden unter: App-Dashboard → Facebook Login for Business → Configurations. Wenn gesetzt, ersetzt die Config-ID die Scopes oben.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
          <a
            href={meta.portal}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-[#2563EB] hover:underline flex items-center gap-1"
            data-testid={`portal-link-${platform}`}
          >
            <ExternalLink className="w-3 h-3" />
            Zum {meta.portalLabel}
          </a>
          <div className="flex items-center gap-2 ml-auto">
            {data.source !== "missing" && (
              <Button
                size="sm"
                variant="outline"
                onClick={validate}
                disabled={checking || saving || deleting}
                className="h-8 text-xs"
                data-testid={`${platform}-validate-btn`}
              >
                {checking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FlaskConical className="w-3 h-3 mr-1" />}
                Prüfen
              </Button>
            )}
            {data.source === "db" && (
              <Button
                size="sm"
                variant="outline"
                onClick={remove}
                disabled={deleting || saving}
                className="h-8 text-xs text-red-600 hover:bg-red-50"
                data-testid={`${platform}-delete-btn`}
              >
                {deleting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Entfernen
              </Button>
            )}
            <Button
              size="sm"
              onClick={save}
              disabled={saving || deleting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 text-xs"
              data-testid={`${platform}-save-btn`}
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Speichern
            </Button>
          </div>
        </div>
        {checkResult && (
          <div
            className={`rounded-md border p-2 text-xs flex items-start gap-2 ${checkResult.valid ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
            data-testid={`${platform}-check-result`}
          >
            {checkResult.valid ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span>{checkResult.message}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
